// Utility functions for floor plan operations
import {
  Booking,
  BookingCompressed,
  BookingVisibilityEnum,
  isBookingCompressed,
  User,
  UserTypeEnum,
} from '@jooxter/api';
import { TFunction } from 'i18next';

/**
 * Checks if a background image has loaded and has valid dimensions
 *
 * @param image - HTML image element
 * @returns True if image is loaded with valid dimensions
 */
export const isImageLoaded = (image?: HTMLImageElement): boolean => {
  return !!(image && image.naturalWidth > 0 && image.naturalHeight > 0);
};

/**
 * Filters out null/undefined values from an array and narrows the type
 *
 * @param value - Value to check
 * @returns True if value is not null/undefined
 */
export const isDefined = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

/**
 * Checks if an array has items
 *
 * @param array - Array to check
 * @returns True if array exists and has at least one item
 */
export const hasItems = <T>(array: T[] | undefined | null): array is T[] => {
  return Array.isArray(array) && array.length > 0;
};

const getOccupantsCount = (
  occupantsCount: number,
  capacity: number,
  t: TFunction<'translation', undefined, 'translation'>
): string => {
  if (occupantsCount > 0) {
    return `${occupantsCount} / ${capacity} ${t<string>('resource-busy', { occupantsCount })}`;
  }
  return '';
};

export const shouldShowSensibleInformation = (user?: User, booking?: Booking | null | BookingCompressed): boolean => {
  if (!(booking && user)) {
    return false;
  }

  if (booking.visibility === BookingVisibilityEnum.Public) {
    return true;
  }

  if (user?.type === UserTypeEnum.Admin) {
    return true;
  }

  if (isBookingCompressed(booking)) {
    return booking.organizer.id === user?.id;
  }

  return (
    booking.organizer.id === user?.id ||
    booking.created.by.id === user?.id ||
    booking.attendees?.internal.some((attendee) => attendee.id === user?.id) ||
    booking.attendees?.external.some((attendee) => attendee.id === user?.id)
  );
};
export const getBookingInformation = (
  user: User,
  t: TFunction<'translation', undefined, 'translation'>,
  isZone: boolean,
  isUnbookable: boolean,
  capacity: number,
  bookings: BookingCompressed[] = [],
  occupantsCount?: number
) => {
  // if resources is zone unbookable, display occupantsCount
  // handle validation
  if (isZone && isUnbookable && occupantsCount && !isNaN(occupantsCount)) {
    return getOccupantsCount(occupantsCount, capacity, t);
  }

  let bookingInformation = '';

  if (bookings.length === 1) {
    const booking = bookings[0];
    const organizerName = shouldShowSensibleInformation(user, booking)
      ? `${booking.organizer.firstname} ${booking.organizer.lastname}`
      : t<string>('private-booking-fields');
    const peoples =
      booking.physicalAttendees && booking.physicalAttendees > 1
        ? ` (+${booking.physicalAttendees - 1} ${t<string>('resource-person')})`
        : '';
    bookingInformation = `${organizerName}${peoples}`;
  } else if (bookings.length > 1) {
    const peoples = bookings.reduce(
      (accumulator, currentValue) =>
        accumulator +
        (currentValue.physicalAttendees && currentValue.physicalAttendees > 0 ? currentValue.physicalAttendees : 1),
      0
    );

    if (peoples > 1) {
      bookingInformation = `${peoples.toString()} ${t<string>('resource-person')}`;
    }
  }

  return bookingInformation;
};
